import type { CardSurvey } from '../types';
import { normalizeTriggerValues } from './pathTriggerValues';

export type ValidationSeverity = 'error' | 'warning';

export interface ValidationIssue {
  severity: ValidationSeverity;
  code: string;
  message: string;
  questionId?: string;
  pathId?: string;
}

/**
 * Validate a card survey and return all issues (errors and warnings).
 * Safe to call with malformed input: returns empty array instead of throwing.
 */
export function validateSurvey(survey: CardSurvey): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (!survey || typeof survey !== 'object') return issues;
  const questions = Array.isArray(survey.questions) ? survey.questions : [];
  const paths = Array.isArray(survey.paths) ? survey.paths : [];

  const questionIds = new Set(questions.map((q) => q.id));

  // --- Question-level validation ---
  questions.forEach((q) => {
    if (q.type !== 'done' && !q.text?.trim()) {
      issues.push({
        severity: 'error',
        code: 'EMPTY_QUESTION_TEXT',
        message: `Question ${q.number} has no text.`,
        questionId: q.id,
      });
    }

    if (q.type === 'radio' || q.type === 'checkbox') {
      const options = q.config?.options ?? [];
      if (options.length === 0) {
        issues.push({
          severity: 'error',
          code: 'NO_OPTIONS',
          message: `Question ${q.number} (${q.type}) has no options.`,
          questionId: q.id,
        });
      }
      const emptyOptions = options
        .map((o, i) => (o?.trim() === '' ? i : -1))
        .filter((i) => i >= 0);
      if (emptyOptions.length > 0) {
        issues.push({
          severity: 'warning',
          code: 'EMPTY_OPTION',
          message: `Question ${q.number} has empty option(s).`,
          questionId: q.id,
        });
      }
      // Duplicate options (identical labels)
      const normalized = options.map((o) => (o ?? '').trim().toLowerCase());
      const seen = new Set<string>();
      const duplicates = new Set<string>();
      normalized.forEach((val) => {
        if (val && seen.has(val)) duplicates.add(val);
        if (val) seen.add(val);
      });
      if (duplicates.size > 0) {
        issues.push({
          severity: 'error',
          code: 'DUPLICATE_OPTIONS',
          message: `Question ${q.number} has duplicate options: ${[...duplicates].join(', ')}. Each option must be unique.`,
          questionId: q.id,
        });
      }
    }

    if (q.type === 'rating' && (!q.config?.rateAmount || q.config.rateAmount < 1)) {
      issues.push({
        severity: 'warning',
        code: 'INVALID_RATING_SCALE',
        message: `Question ${q.number} (rating) should have a positive scale.`,
        questionId: q.id,
      });
    }
  });

  // --- Path validation ---
  paths.forEach((path) => {
    const values = normalizeTriggerValues(path.trigger.values);

    const triggerQuestion = questions.find((q) => q.id === path.trigger.questionId);
    if (!triggerQuestion) {
      issues.push({
        severity: 'error',
        code: 'PATH_TRIGGER_MISSING',
        message: `Path "${path.name}" references a trigger question that does not exist.`,
        pathId: path.id,
      });
    } else {
      if (triggerQuestion.type !== 'radio' && triggerQuestion.type !== 'checkbox') {
        issues.push({
          severity: 'warning',
          code: 'PATH_TRIGGER_NOT_CHOICE',
          message: `Path "${path.name}" trigger question is not single/multiple choice.`,
          pathId: path.id,
          questionId: path.trigger.questionId,
        });
      } else {
        const options = triggerQuestion.config?.options ?? [];
        const normalizedOptions = new Set(options.map((o) => (o ?? '').trim()));
        const missing = values.filter(
          (v) => !normalizedOptions.has((v ?? '').trim())
        );
        if (missing.length > 0) {
          issues.push({
            severity: 'error',
            code: 'PATH_TRIGGER_VALUES_MISMATCH',
            message: `Path "${path.name}" trigger values [${missing.join(', ')}] are not in the question options.`,
            pathId: path.id,
            questionId: path.trigger.questionId,
          });
        }
      }
    }

    if (values.length === 0) {
      issues.push({
        severity: 'error',
        code: 'PATH_NO_TRIGGER_VALUES',
        message: `Path "${path.name}" has no trigger values selected.`,
        pathId: path.id,
      });
    }

    path.questionIds.forEach((qId) => {
      if (!questionIds.has(qId)) {
        issues.push({
          severity: 'error',
          code: 'PATH_QUESTION_MISSING',
          message: `Path "${path.name}" references question ID that does not exist.`,
          pathId: path.id,
          questionId: qId,
        });
      }
    });

    const pathQuestionIds = new Set(path.questionIds);
    questions
      .filter((q) => q.pathId === path.id)
      .forEach((q) => {
        if (!pathQuestionIds.has(q.id)) {
          issues.push({
            severity: 'warning',
            code: 'PATH_QUESTION_NOT_IN_IDS',
            message: `Question ${q.number} has pathId "${path.name}" but is not in path questionIds.`,
            pathId: path.id,
            questionId: q.id,
          });
        }
      });
  });

  // --- Orphan pathId on questions ---
  const pathIds = new Set(paths.map((p) => p.id));
  questions.forEach((q) => {
    if (q.pathId && !pathIds.has(q.pathId)) {
      issues.push({
        severity: 'error',
        code: 'QUESTION_ORPHAN_PATH',
        message: `Question ${q.number} references path that does not exist.`,
        questionId: q.id,
      });
    }
  });

  return issues;
}

export function getErrors(issues: ValidationIssue[]): ValidationIssue[] {
  return issues.filter((i) => i.severity === 'error');
}

export function getWarnings(issues: ValidationIssue[]): ValidationIssue[] {
  return issues.filter((i) => i.severity === 'warning');
}
